/**
 * Walking the selected images and asking Earth Engine for a waterline from each.
 * Every image is a separate request: the waterline depends on that image's tide reading, so there is
 * nothing to batch. What there is to choose is how many are in the air at once.
 * The original loop sent one, waited for the answer, then sent the next. Each answer is a network
 * round trip plus server-side computation, so twelve images cost twelve latencies end to end, with
 * the browser idle for almost all of it - which is the stall the user sees on this step. The comment
 * said this was to avoid overwhelming Earth Engine, but a handful of concurrent evaluates is well
 * inside what it serves; the real ceiling is the browser's own limit of connections per host.
 * So a few run at a time and a new one is sent as each finishes, which turns twelve latencies into
 * about three.
 * Results then arrive out of order. That is handled where it matters rather than by giving the
 * ordering up: the colour belongs to the image's index, not to its arrival, and anything a caller
 * keeps per image is placed by index through {@link insertByIndex}.
 */

/**
 * @constant WATERLINE_CONCURRENCY
 * @description How many images to have in flight at once. Four is comfortably under the browser's
 * per-host connection limit, leaving room for the map tiles loading at the same time.
 * @memberof module:Intertidal
 */
export const WATERLINE_CONCURRENCY = 4;

/**
 * @function insertByIndex
 * @description Places an entry in a list by the image index it belongs to, so a list built from
 * results that arrive out of order still reads in image order.
 * @param {Array<Object>} list - The list so far.
 * @param {Object} entry - The entry to place; its `imageIndex` decides where.
 * **Called by:
 * @see {ProcessingIntertidalPage} - building the waterline layer list.
 * @memberof module:Intertidal
 * @returns {Array<Object>} A new list, ordered by `imageIndex`.
 */
export const insertByIndex = (list, entry) =>
  [...list, entry].sort((a, b) => (a.imageIndex ?? 0) - (b.imageIndex ?? 0));

/**
 * @function runWaterlines
 * @description Runs one Earth Engine request per image, keeping a few in flight, and reports each
 * result as it lands.
 * A request that fails is still a request that finished: it is reported to `onResult` with its error
 * and the run carries on to the next image. One image whose waterline cannot be computed must not
 * cost the user the other eleven.
 * @param {Object} options
 * @param {Number} options.total - How many images to process.
 * @param {Function} options.request - `(index) => eeObject` - builds the request for one image. The
 *  returned object is evaluated by this function.
 * @param {Function} options.onResult - `(index, value, error) => void` - called once per image, in
 *  arrival order.
 * @param {Function} [options.onProgress] - `({completed, total}) => void` - called after each result.
 * @param {Function} [options.onDone] - called once, after the last image.
 * @param {Number} [options.concurrency] - How many to keep in flight.
 * **Called by:
 * @see {WaterlineIdentificationAndLevelAssigment}
 * @see {ProcessingIntertidalPage}
 * @memberof module:Intertidal
 * @returns {void}
 */
export const runWaterlines = ({
  total,
  request,
  onResult,
  onProgress,
  onDone,
  concurrency = WATERLINE_CONCURRENCY,
}) => {
  if (!total) {
    onDone?.();
    return;
  }

  let next = 0;
  let completed = 0;

  const finish = (index, value, error) => {
    try {
      onResult(index, value, error);
    } catch (problem) {
      console.error(`[waterlineRunner] a imagem ${index + 1} não pôde ser desenhada:`, problem);
    }
    completed += 1;
    onProgress?.({ completed, total });
    if (completed >= total) {
      onDone?.();
      return;
    }
    launch();
  };

  const launch = () => {
    if (next >= total) return;
    const index = next;
    next += 1;
    try {
      request(index).evaluate((value, error) => finish(index, value, error));
    } catch (problem) {
      finish(index, null, problem);
    }
  };

  for (let i = 0; i < Math.min(concurrency, total); i += 1) launch();
};
